import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  DatePicker,
  Form,
  Select,
  Space,
  Button,
  Table,
  message,
  Row,
  Col,
} from "antd";
import {
  LineChartOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import ReactECharts from "echarts-for-react";
import dayjs, { Dayjs } from "dayjs";
import MainLayout from "../../components/layout/MainLayout";
import { StatService, OrgService } from "../../services";
import { StatDailyItem } from "../../types/stat";
import { Org } from "../../types/org";
import ErrorHandler from "../../utils/errorHandler";
import TokenManager from "../../utils/tokenManager";
import "./StatDashboard.scss";

const { Option } = Select;

const buildLineOption = (
  title: string,
  data: StatDailyItem[],
  valueName: string
) => {
  const days = data.map((d) => d.day);
  const cnts = data.map((d) => d.cnt);
  const totals = data.map((d) => d.total);

  return {
    title: {
      text: title,
      left: "left",
      top: 0,
      textStyle: { fontSize: 14 },
    },
    tooltip: {
      trigger: "axis",
      formatter: (params: any[]) => {
        if (!params || params.length === 0) {
          return "";
        }

        const day = params[0]?.axisValue || "";
        const cntItem = params.find((p) => p.seriesName === "次数");
        const totalItem = params.find((p) => p.seriesName === valueName);

        const cnt = cntItem?.data ?? 0;
        const total = totalItem?.data ?? 0;

        return `${day}<br/>次数: ${cnt}<br/>${valueName}: ${total}`;
      },
    },
    legend: {
      data: ["次数", valueName],
      top: 28,
      left: "center",
    },
    grid: {
      left: 40,
      right: 40,
      top: 60,
      bottom: 30,
    },
    xAxis: {
      type: "category",
      data: days,
      boundaryGap: false,
      axisLabel: {
        // 仅显示“几号”，不展示年份和月份
        formatter: (value: string) => dayjs(value).format("DD"),
      },
    },
    yAxis: [
      {
        type: "value",
        name: "次数",
      },
      {
        type: "value",
        name: valueName,
      },
    ],
    series: [
      {
        name: "次数",
        type: "line",
        smooth: true,
        data: cnts,
        yAxisIndex: 0,
        lineStyle: {
          width: 2,
          color: "#1890ff", // 蓝色，保持当前风格
        },
        itemStyle: {
          color: "#1890ff",
        },
      },
      {
        name: valueName,
        type: "line",
        smooth: true,
        data: totals,
        yAxisIndex: 1,
        lineStyle: {
          width: 3,
          color: "#fa541c", // 高亮的橙红色
        },
        itemStyle: {
          color: "#fa541c",
        },
        emphasis: {
          focus: "series",
        },
      },
    ],
  };
};

const StatDashboard: React.FC = () => {
  const [form] = Form.useForm();
  const [orgOptions, setOrgOptions] = useState<Org[]>([]);
  const [loading, setLoading] = useState(false);

  const [rechargeData, setRechargeData] = useState<StatDailyItem[]>([]);
  const [consumeData, setConsumeData] = useState<StatDailyItem[]>([]);

  const [rechargePagination, setRechargePagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [consumePagination, setConsumePagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // 获取当前用户角色和组织
  const userInfo = useMemo(() => {
    const token = TokenManager.getAccessToken();
    if (!token) return null;
    return TokenManager.getUserFromToken(token);
  }, []);

  const roles: string[] = userInfo?.roles || [];
  const isAdmin = roles.includes("admin");
  const orgIdFromToken = userInfo?.orgId as number | undefined;

  // 加载组织列表（仅 admin 需要）
  const loadOrgOptions = async () => {
    if (!isAdmin) return;
    try {
      const res = await OrgService.getOrgList({ limit: 1000, page: 1 });
      if (res.code === 0 && res.data) {
        setOrgOptions(res.data.data || []);
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    }
  };

  const normalizeMonth = (month?: Dayjs) =>
    month ? month.format("YYYY-MM") : dayjs().format("YYYY-MM");

  const buildQueryParams = (values?: any) => {
    const month = normalizeMonth(values?.month);
    let orgId = values?.orgId as number | undefined;

    // org_manager 的 orgId 由后端强制限制，这里仅做前端提示控制
    if (!isAdmin && orgIdFromToken) {
      orgId = orgIdFromToken;
    }

    return {
      month,
      orgId,
    };
  };

  const loadStats = async (values?: any) => {
    setLoading(true);
    try {
      const baseParams = buildQueryParams(values);

      const [rechargeRes, consumeRes] = await Promise.all([
        StatService.getRechargeMonthly(baseParams),
        StatService.getConsumeMonthly(baseParams),
      ]);

      if (rechargeRes.code === 0 && rechargeRes.data) {
        const list = (rechargeRes.data || []).map((item) => ({
          ...item,
          // 将后端返回的 ISO 时间格式标准化为 YYYY-MM-DD
          day: dayjs(item.day).format("YYYY-MM-DD"),
        }));
        setRechargeData(list);
        setRechargePagination((prev) => ({ ...prev, current: 1 }));
      } else {
        message.error(rechargeRes.msg || "获取充值统计失败");
      }

      if (consumeRes.code === 0 && consumeRes.data) {
        const list = (consumeRes.data || []).map((item) => ({
          ...item,
          day: dayjs(item.day).format("YYYY-MM-DD"),
        }));
        setConsumeData(list);
        setConsumePagination((prev) => ({ ...prev, current: 1 }));
      } else {
        message.error(consumeRes.msg || "获取消耗统计失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  // 充值总算力 & 消耗总量 的总和
  const rechargeTotalSum = useMemo(
    () =>
      rechargeData.reduce((sum, item) => sum + (Number(item.total) || 0), 0),
    [rechargeData]
  );

  const consumeTotalSum = useMemo(
    () => consumeData.reduce((sum, item) => sum + (Number(item.total) || 0), 0),
    [consumeData]
  );

  const formatNumber = (value: number) =>
    value.toLocaleString("zh-CN", { maximumFractionDigits: 2 });

  // 初始化：默认当前月
  useEffect(() => {
    form.setFieldsValue({ month: dayjs() });
    loadOrgOptions();
    loadStats({ month: dayjs() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (values: any) => {
    loadStats(values);
  };

  const handleReset = () => {
    form.resetFields();
    const defaultMonth = dayjs();
    form.setFieldsValue({ month: defaultMonth });
    loadStats({ month: defaultMonth });
  };

  const rechargeColumns = [
    {
      title: "日期",
      dataIndex: "day",
      key: "day",
      width: 140,
    },
    {
      title: "充值次数",
      dataIndex: "cnt",
      key: "cnt",
      width: 100,
    },
    {
      title: "充值总算力",
      dataIndex: "total",
      key: "total",
      width: 140,
      render: (amount: number) => `${amount.toFixed(2)}`,
    },
  ];

  const consumeColumns = [
    {
      title: "日期",
      dataIndex: "day",
      key: "day",
      width: 140,
    },
    {
      title: "消耗次数",
      dataIndex: "cnt",
      key: "cnt",
      width: 100,
    },
    {
      title: "消耗总量",
      dataIndex: "total",
      key: "total",
      width: 140,
      render: (amount: number) => amount,
    },
  ];

  return (
    <MainLayout title="数据统计">
      <div className="stat-dashboard">
        {/* 查询条件 */}
        <Card className="search-card" style={{ marginBottom: 16 }}>
          <Form
            form={form}
            layout="inline"
            onFinish={handleSearch}
            initialValues={{ month: dayjs() }}
          >
            <Form.Item
              name="month"
              label="月份"
              rules={[{ required: true, message: "请选择月份" }]}
            >
              <DatePicker
                picker="month"
                placeholder="请选择月份"
                style={{ width: 160 }}
              />
            </Form.Item>

            {isAdmin && (
              <Form.Item name="orgId" label="组织">
                <Select
                  placeholder="请选择组织（可选）"
                  allowClear
                  style={{ width: 220 }}
                  showSearch
                  optionFilterProp="children"
                >
                  {orgOptions.map((org) => (
                    <Option key={org.id} value={org.id}>
                      {org.name} (ID: {org.id})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SearchOutlined />}
                >
                  搜索
                </Button>
                <Button onClick={handleReset} icon={<ReloadOutlined />}>
                  重置
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        {/* 统计展示 */}
        <Row gutter={16}>
          <Col span={12}>
            <Card
              title={
                <Space>
                  <LineChartOutlined />
                  <span>充值统计</span>
                </Space>
              }
            >
              <ReactECharts
                style={{ height: 300 }}
                option={buildLineOption(
                  "每日充值统计",
                  rechargeData,
                  "充值总算力"
                )}
                notMerge
                lazyUpdate
              />
              <div style={{ marginTop: 8, fontSize: 13 }}>
                当月充值总算力：{" "}
                <strong>{formatNumber(rechargeTotalSum)}</strong>
              </div>
              <Table
                style={{ marginTop: 16 }}
                columns={rechargeColumns}
                dataSource={rechargeData}
                rowKey="day"
                loading={loading}
                pagination={{
                  current: rechargePagination.current,
                  pageSize: rechargePagination.pageSize,
                  total: rechargeData.length,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  onChange: (page, pageSize) => {
                    setRechargePagination({
                      current: page,
                      pageSize: pageSize || rechargePagination.pageSize,
                    });
                  },
                }}
              />
            </Card>
          </Col>

          <Col span={12}>
            <Card
              title={
                <Space>
                  <LineChartOutlined />
                  <span>消耗统计</span>
                </Space>
              }
            >
              <ReactECharts
                style={{ height: 300 }}
                option={buildLineOption(
                  "每日消耗统计",
                  consumeData,
                  "消耗总量"
                )}
                notMerge
                lazyUpdate
              />
              <div style={{ marginTop: 8, fontSize: 13 }}>
                当月消耗总量： <strong>{formatNumber(consumeTotalSum)}</strong>
              </div>
              <Table
                style={{ marginTop: 16 }}
                columns={consumeColumns}
                dataSource={consumeData}
                rowKey="day"
                loading={loading}
                pagination={{
                  current: consumePagination.current,
                  pageSize: consumePagination.pageSize,
                  total: consumeData.length,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  onChange: (page, pageSize) => {
                    setConsumePagination({
                      current: page,
                      pageSize: pageSize || consumePagination.pageSize,
                    });
                  },
                }}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </MainLayout>
  );
};

export default StatDashboard;
